import { useCallback, useEffect, useRef, useState } from 'react';

const ImageCarousel = ({
  images,
  autoplayInterval = 3000,
}: {
  images: { src: string; alt: string; id: number }[];
  autoplayInterval?: number;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-play functionality
  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
  }, [images.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  // Auto-play effect
  useEffect(() => {
    if (!isAutoPlaying) return;

    timerRef.current = setInterval(() => {
      nextSlide();
    }, autoplayInterval);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isAutoPlaying, nextSlide, currentIndex]);

  // Pause autoplay on hover
  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
  };

  const handleMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  // Get previous and next indices
  const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
  const nextIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div
        className="relative flex items-center justify-center"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Previous image (blurred) */}
        <div className="absolute left-0 w-40 h-30 sm:w-56 sm:h-42 md:w-64 md:h-48 lg:w-80 lg:h-60 xl:w-96 xl:h-72 rounded-2xl overflow-hidden shadow-lg opacity-60 transform -translate-x-8 z-10 transition-transform duration-1000 fade-in-out">
          <img
            src={images[prevIndex].src}
            alt={images[prevIndex].alt}
            className="w-full h-full object-cover filter blur-sm transition-transform duration-1000 ease-in-out"
          />
        </div>

        {/* Current image (center, focused) */}
        <div className="relative w-60 h-45 sm:w-80 sm:h-60 md:w-[400px] md:h-[300px] lg:w-[600px] lg:h-[450px] xl:w-[800px] xl:h-[600px] rounded-2xl overflow-hidden shadow-2xl z-20 transition-transform duration-1000 fade-in-out">
          <img
            src={images[currentIndex].src}
            alt={images[currentIndex].alt}
            className="w-full h-full object-cover transition-all duration-500 ease-in-out transition-transform duration-1000 ease-in-out"
          />
        </div>

        {/* Next image (blurred) */}
        <div className="absolute right-0 w-40 h-30 sm:w-56 sm:h-42 md:w-64 md:h-48 lg:w-80 lg:h-60 xl:w-96 xl:h-72 rounded-2xl overflow-hidden shadow-lg opacity-60 transform translate-x-8 z-10 transition-transform duration-1000 fade-in-out">
          <img
            src={images[nextIndex].src}
            alt={images[nextIndex].alt}
            className="w-full h-full object-cover filter blur-sm transition-transform duration-1000 ease-in-out"
          />
        </div>
      </div>
      {/* Dots indicator - outside the image area */}
      <div className="flex justify-center mt-6 space-x-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`rounded-full transition-all duration-300 ${index === currentIndex
              ? 'h-2 w-8 md:w-16 md:h-3 bg-[#1CA7E5]'
              : 'w-2 h-2 md:w-3 md:h-3 bg-[#B3D6E5]'
              }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageCarousel;
